export type Source = {
  key: string
  title: string
  url: string
}

export type Collection = {
  key: string
  title: string
  source: Source
  url: string
  count: number
  previewURL: string
}

export type Illustration = {
  key: string
  title: string
  keywords: string[]
  category: string[]
  collection: Collection
  source: Source
  previewURL: string
} & (
  | { action: 'download'; downloadURL: string }
  | { action: 'use'; useURL: string }
)

export const sources: Record<string, Source> = {
  drawkit: {
    key: 'drawkit',
    title: 'DrawKit',
    url: 'https://www.drawkit.io/',
  },
  icons8: {
    key: 'icons8',
    title: 'icons8',
    url: 'https://icons8.com/',
  },
  typedream: {
    key: 'typedream',
    title: 'Typedream',
    url: 'https://typedream.com/',
  },
};

export const collections: Record<string, Collection> = {
  typedream_gradients: {
    key: 'typedream_gradients',
    title: 'Gradients',
    source: sources.typedream,
    url: 'https://typedream.com/',
    count: 104,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-7.gif',
  },
  icons8_3dhands: {
    key: 'icons8_3dhands',
    title: '3D Hands Fun & Wild',
    source: sources.icons8,
    url: 'https://icons8.com/illustrations/style--3d-hands-fun-and-wild',
    count: 18,
    previewURL:
      'https://ouch-cdn2.icons8.com/AeOokkhSuFJOYqNJw336b1H4nyh97ehsKP2ZekJt2iA/rs:fit:196:163/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNzEz/L2VmYzlmZWEwLTc5/ZjItNGZiMS05ZmJi/LTllNjRiN2RkM2E1/Yi5wbmc.png',
  },
  drawkit_wfh: {
    key: 'drawkit_wfh',
    title: 'Work From Home',
    source: sources.drawkit,
    url: 'https://www.drawkit.io/product/working-from-home-illustrations',
    count: 10,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_1.svg',
  },
  drawkit_education: {
    key: 'drawkit_education',
    title: 'Education',
    source: sources.drawkit,
    url: 'https://www.drawkit.io/product/education-free-vector-illustrations',
    count: 10,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/5-SCENE.svg',
  },
};

export const illustrations: Record<string, Illustration> = {
  drawkit_wfh__women_laptop: {
    key: 'drawkit_wfh__women_laptop',
    title: 'Woman working',
    keywords: ['wfh', 'work from home', 'laptop', 'woman', 'desk', 'work'],
    category: ['people', 'work'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_1.svg',
    action: 'download',
  },
  drawkit_wfh__man_scooter: {
    key: 'drawkit_wfh__man_scooter',
    title: 'Man on a scooter',
    keywords: ['wfh', 'work from home', 'man', 'scooter', 'go', 'work'],
    category: ['people'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_2.svg',
    action: 'download',
  },
  drawkit_wfh__sing_window: {
    key: 'drawkit_wfh__sing_window',
    title: 'Singing on the window',
    keywords: [
      'wfh',
      'work from home',
      'man',
      'woman',
      'singing',
      'love',
      'coffee',
      'window',
    ],
    category: ['people'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_3.svg',
    action: 'download',
  },
  drawkit_wfh__netflix_chill: {
    key: 'drawkit_wfh__netflix_chill',
    title: 'Man watching Netflix',
    keywords: [
      'wfh',
      'work from home',
      'man',
      'sofa',
      'couch',
      'netflix',
      'chill',
      'break',
      'popcorn',
    ],
    category: ['people'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_4.svg',
    action: 'download',
  },
  drawkit_wfh__yoga: {
    key: 'drawkit_wfh__yoga',
    title: 'Yoga at home',
    keywords: ['wfh', 'work from home', 'woman', 'yoga', 'home', 'exercise'],
    category: ['people'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_5.svg',
    action: 'download',
  },
  drawkit_wfh__birthday: {
    key: 'drawkit_wfh__birthday',
    title: 'Birthday celebration',
    keywords: [
      'wfh',
      'work from home',
      'woman',
      'birthday',
      'zoom',
      'meeting',
      'online',
      'event',
    ],
    category: ['people'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_6.svg',
    action: 'download',
  },
  drawkit_wfh__play: {
    key: 'drawkit_wfh__play',
    title: 'Playing Jenga',
    keywords: [
      'wfh',
      'work from home',
      'woman',
      'jenga',
      'play',
      'break',
      'wine',
      'chill',
    ],
    category: ['people'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_7.svg',
    action: 'download',
  },
  drawkit_wfh__woman_laptop_flower: {
    key: 'drawkit_wfh__woman_laptop_flower',
    title: 'Working with a laptop',
    keywords: [
      'wfh',
      'work from home',
      'woman',
      'work',
      'laptop',
      'window',
      'flower',
    ],
    category: ['people', 'work'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_8.svg',
    action: 'download',
  },
  drawkit_wfh__online_meeting: {
    key: 'drawkit_wfh__online_meeting',
    title: 'Conference call',
    keywords: [
      'wfh',
      'work from home',
      'woman',
      'work',
      'man',
      'conference',
      'meeting',
      'zoom',
      'online',
    ],
    category: ['people', 'work'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_9.svg',
    action: 'download',
  },
  drawkit_wfh__online_meeting_cooking: {
    key: 'drawkit_wfh__online_meeting_cooking',
    title: 'Cooking during meeting',
    keywords: [
      'wfh',
      'work from home',
      'woman',
      'work',
      'cooking',
      'conference',
      'meeting',
      'zoom',
      'online',
    ],
    category: ['people', 'work'],
    collection: collections.drawkit_wfh,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/working-from-home-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_wfh/wfh_10.svg',
    action: 'download',
  },
  drawkit_education__graduation: {
    key: 'drawkit_education__graduation',
    title: 'Graduation',
    keywords: [
      'education',
      'graduation',
      'woman',
      'man',
      'student',
      'diploma',
      'college',
      'university',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/1-SCENE.svg',
    action: 'download',
  },
  drawkit_education__graduation_party: {
    key: 'drawkit_education__graduation_party',
    title: 'Graduation Party',
    keywords: [
      'education',
      'graduation',
      'woman',
      'man',
      'student',
      'diploma',
      'college',
      'university',
      'party',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/2-SCENE.svg',
    action: 'download',
  },
  drawkit_education__studying: {
    key: 'drawkit_education__studying',
    title: 'Studying',
    keywords: [
      'education',
      'man',
      'studying',
      'student',
      'school',
      'college',
      'books',
      'coffee',
      'desk',
      'laptop',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/3-SCENE.svg',
    action: 'download',
  },
  drawkit_education__group_study: {
    key: 'drawkit_education__group_study',
    title: 'Online Meeting',
    keywords: [
      'education',
      'woman',
      'meeting',
      'online',
      'student',
      'school',
      'studying',
      'zoom',
      'desk',
      'laptop',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/4-SCENE.svg',
    action: 'download',
  },
  drawkit_education__online_course: {
    key: 'drawkit_education__online_course',
    title: 'Online Course',
    keywords: [
      'education',
      'woman',
      'man',
      'online',
      'student',
      'school',
      'studying',
      'zoom',
      'course',
      'desk',
      'laptop',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/5-SCENE.svg',
    action: 'download',
  },
  drawkit_education__library: {
    key: 'drawkit_education__library',
    title: 'Library',
    keywords: [
      'education',
      'library',
      'man',
      'books',
      'student',
      'school',
      'studying',
      'college',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/6-SCENE.svg',
    action: 'download',
  },
  drawkit_education__hangout: {
    key: 'drawkit_education__hangout',
    title: 'Hanging Out',
    keywords: [
      'education',
      'hangout',
      'man',
      'woman',
      'student',
      'school',
      'studying',
      'college',
      'dorm',
      'fraternity',
      'laptop',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/7-SCENE.svg',
    action: 'download',
  },
  drawkit_education__tutor: {
    key: 'drawkit_education__tutor',
    title: 'Tutoring',
    keywords: [
      'education',
      'tutoring',
      'man',
      'woman',
      'student',
      'school',
      'studying',
      'college',
      'kid',
      'girl',
      'books',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/8-SCENE.svg',
    action: 'download',
  },
  drawkit_education__learn_online: {
    key: 'drawkit_education__learn_online',
    title: 'Learning Online',
    keywords: [
      'education',
      'tutoring',
      'man',
      'woman',
      'student',
      'school',
      'studying',
      'college',
      'learning',
      'girl',
      'online',
      'meeting',
      'zoom',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/9-SCENE.svg',
    action: 'download',
  },
  drawkit_education__exam: {
    key: 'drawkit_education__exam',
    title: 'Taking an Exam',
    keywords: [
      'education',
      'exam',
      'woman',
      'student',
      'school',
      'studying',
      'college',
      'girl',
      'time',
      'desk',
      'backpack',
    ],
    category: ['people', 'student'],
    collection: collections.drawkit_education,
    source: sources.drawkit,
    downloadURL:
      'https://www.drawkit.io/product/education-free-vector-illustrations',
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/drawkit_education/10-SCENE.svg',
    action: 'download',
  },
  icons8_3dhands__hand_hello_white: {
    key: 'icons8_3dhands__hand_hello_white',
    title: 'Hello',
    keywords: ['hand', 'hello', 'greeting', 'waving', 'welcome', 'hi'],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-206',
    previewURL:
      'https://ouch-cdn2.icons8.com/zKehoopfysb89K76Jr2RQkq_6lsgKL_yFfYd0DyEjXg/rs:fit:196:270/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvOTUy/LzFhOTY3ODViLTgw/NjctNDhhYi1iNzFh/LTIzYjI4MTY4YjRh/NS5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_hello_brown: {
    key: 'icons8_3dhands__hand_hello_brown',
    title: 'Hello',
    keywords: ['hand', 'hello', 'greeting', 'waving', 'welcome', 'hi'],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-203',
    previewURL:
      'https://ouch-cdn2.icons8.com/CY5C8BLAkxx4GDDSk_2DRCMlYoaB6Bsl5U6PLgCNnEE/rs:fit:196:270/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvODA0/L2I0MjhiNjM2LWYw/ODEtNGQ5Yy04MzFh/LWY3YzhlYjU3MTgy/Zi5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_hello_black: {
    key: 'icons8_3dhands__hand_hello_black',
    title: 'Hello',
    keywords: ['hand', 'hello', 'greeting', 'waving', 'welcome', 'hi'],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-205',
    previewURL:
      'https://ouch-cdn2.icons8.com/2mO08QvzewZkyHYFI905oaXdxQJ9gCjGZs69pq6kcGU/rs:fit:196:270/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvMTU4/L2U2NzJhYTk0LTMx/MDEtNDYxNS1iMDI5/LWJmYTViMzg1YWY1/Zi5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_heart_white: {
    key: 'icons8_3dhands__hand_heart_white',
    title: 'Heart Hands',
    keywords: ['hand', 'heart', 'love', 'valentine', 'romance', 'romantic'],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-283',
    previewURL:
      'https://ouch-cdn2.icons8.com/XuZtRS4HuaqBrDk5_WpIksSf1SD7jLtm9ujrW-klg9Y/rs:fit:196:99/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvMjc5/Lzk2ZWRjMDIxLTc1/N2EtNDYzNy1hNGUz/LTI3ZDkxNjE3ZTRl/OS5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_heart_brown: {
    key: 'icons8_3dhands__hand_heart_brown',
    title: 'Heart Hands',
    keywords: ['hand', 'heart', 'love', 'valentine', 'romance', 'romantic'],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-280',
    previewURL:
      'https://ouch-cdn2.icons8.com/aS9bHLQiORpbFOy3Ny3NctR7keML1PuJqFoLcdB7Ucg/rs:fit:196:99/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNTk3/LzlmZjY2YmNiLWE5/MTUtNDRmMi05ZWRl/LWRmMTY0NTE5MWFk/Ny5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_heart_black: {
    key: 'icons8_3dhands__hand_heart_black',
    title: 'Heart Hands',
    keywords: ['hand', 'heart', 'love', 'valentine', 'romance', 'romantic'],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-282',
    previewURL:
      'https://ouch-cdn2.icons8.com/d4-4sJpfyI3u9zSFgo7v5Ho8PE08OuMN7zFstrDc0lQ/rs:fit:196:99/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvMzgy/L2FiOWQ3M2UzLWU1/ZGEtNDdjNi1iYjM4/LTFkN2ViZDliNjMw/OS5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_iphone_white: {
    key: 'icons8_3dhands__hand_iphone_white',
    title: 'Hands with phone',
    keywords: [
      'hand',
      'business',
      'smartphone',
      'handphone',
      'call',
      'social media',
    ],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-294',
    previewURL:
      'https://ouch-cdn2.icons8.com/HfHb9Hvg0ol1jmJDQJd7ulxZyqPE0tgZPEH_eHf3QX4/rs:fit:196:190/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvMzgv/YWRhZGI0ZTYtNmNm/Ni00YWU4LTgyNmUt/MGI0OTEwZDA4NDIy/LnBuZw.png',
    action: 'download',
  },
  icons8_3dhands__hand_iphone_brown: {
    key: 'icons8_3dhands__hand_iphone_brown',
    title: 'Hands with phone',
    keywords: [
      'hand',
      'business',
      'smartphone',
      'handphone',
      'call',
      'social media',
    ],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-291',
    previewURL:
      'https://ouch-cdn2.icons8.com/42ICMyVtgujOzLRBMcFjbRbm5-Kh_EQh9WAR_CCeLnI/rs:fit:196:190/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvMTIx/LzNmYWJhZWFjLWQ4/ZGItNDRlNi1hMjJk/LTlkM2I5ZWUwNjA5/My5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_iphone_black: {
    key: 'icons8_3dhands__hand_iphone_black',
    title: 'Hands with phone',
    keywords: [
      'hand',
      'business',
      'smartphone',
      'handphone',
      'call',
      'social media',
    ],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-293',
    previewURL:
      'https://ouch-cdn2.icons8.com/7kgAQwpE0I8YShkWmKYKhi49EcvIAK0-FtMgR_AEl9k/rs:fit:196:190/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNzM0/Lzg0N2NlNGQ5LTg2/NDEtNDQ2ZC1hOTE1/LTY0MTE2ZjE3NmU5/Ni5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_high_five_black_brown: {
    key: 'icons8_3dhands__hand_high_five_black_brown',
    title: 'High Five',
    keywords: [
      'hand',
      'success',
      'hello',
      'done',
      'business',
      'greeting',
      'high five',
      'clap',
    ],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-322',
    previewURL:
      'https://ouch-cdn2.icons8.com/PL8fxW8vJRefcwKIK9JA6oycAolI8XCJT2lIDEu-LUs/rs:fit:196:182/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvMzc5/L2I5ZDYwNmI3LWY4/ZTYtNDI5NC1iNjE0/LThlOTE0NDc2MWJh/MS5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_high_five_black_black: {
    key: 'icons8_3dhands__hand_high_five_black_black',
    title: 'High Five',
    keywords: [
      'hand',
      'success',
      'hello',
      'done',
      'business',
      'greeting',
      'high five',
      'clap',
    ],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-326',
    previewURL:
      'https://ouch-cdn2.icons8.com/ooGPPLHVUqufOokTJsZx6wBcq4SZtWjo12jSgm-mWOU/rs:fit:196:182/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNjI0/L2E0NzA1MGZmLTIy/Y2ItNGFlYi05NjRl/LTVhMTE3ODZhNzgz/ZS5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hand_high_five_white_white: {
    key: 'icons8_3dhands__hand_high_five_white_white',
    title: 'High Five',
    keywords: [
      'hand',
      'success',
      'hello',
      'done',
      'business',
      'greeting',
      'high five',
      'clap',
    ],
    category: ['hand'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-332',
    previewURL:
      'https://ouch-cdn2.icons8.com/CmTC0fo_shuhA-R4nFmAPUyYWwciqkOQ6XfRoUUtyTE/rs:fit:196:182/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvOTU3/LzI2NmY3NDhjLTU4/YWUtNGY3OS1hODM2/LWI1MTJkYTM1MTgx/YS5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hands_soap_white: {
    key: 'icons8_3dhands__hands_soap_white',
    title: 'Hands Sanitizer',
    keywords: [
      'health',
      'hands',
      'palm',
      'soap',
      'coronavirus',
      'covid',
      'wash',
      'hygiene',
    ],
    category: ['hand', 'covid'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-109',
    previewURL:
      'https://ouch-cdn2.icons8.com/2GbpxlMlQjz7ufYlmMd1CuNNVn6zmq56xk2Pn2Nlim8/rs:fit:196:174/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNTE3/LzE3NjdmZTg4LTEx/OTEtNDViOS1iN2Fj/LTA5ZTc3ZGUzYmFj/Mi5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hands_soap_brown: {
    key: 'icons8_3dhands__hands_soap_brown',
    title: 'Hands Sanitizer',
    keywords: [
      'health',
      'hands',
      'palm',
      'soap',
      'coronavirus',
      'covid',
      'wash',
      'hygiene',
    ],
    category: ['hand', 'covid'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-106',
    previewURL:
      'https://ouch-cdn2.icons8.com/_MY3gU9DdY42_fvyMBak35UbGxk_uauGbP0YV4yVKr8/rs:fit:196:174/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvOTM0/LzAzZmVlMDU2LWJj/YjQtNDcwZi05NzJk/LWE4NTQ4ZTczMjNh/ZS5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hands_soap_black: {
    key: 'icons8_3dhands__hands_soap_black',
    title: 'Hands Sanitizer',
    keywords: [
      'health',
      'hands',
      'palm',
      'soap',
      'coronavirus',
      'covid',
      'wash',
      'hygiene',
    ],
    category: ['hand', 'covid'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-108',
    previewURL:
      'https://ouch-cdn2.icons8.com/CsSGvsUy8DITUp2wVrsd3ExBdpzjT6kNMgnnxDXf8BY/rs:fit:196:174/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNzYw/Lzc3ZmEzMjQ3LWY5/M2YtNGM1MS04YmVl/LTA2NTQ1YmFiYzlm/NC5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hands_sanitizer_white: {
    key: 'icons8_3dhands__hands_sanitizer_white',
    title: 'Hands Sanitizer',
    keywords: [
      'health',
      'hands',
      'palm',
      'disinfectant',
      'coronavirus',
      'covid',
      'sanitizer',
      'hygiene',
      'bottle',
    ],
    category: ['hand', 'covid'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-131',
    previewURL:
      'https://ouch-cdn2.icons8.com/uJiR2qoqvub2ts2lzeQ67KB4xEP3RU4VwcwknVebENo/rs:fit:196:116/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNjcw/LzdmYTkzODk3LTEz/OWMtNDEyMC04ZDhj/LWRjOTNiZWMwYWQy/OC5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hands_sanitizer_brown: {
    key: 'icons8_3dhands__hands_sanitizer_brown',
    title: 'Hands Sanitizer',
    keywords: [
      'health',
      'hands',
      'palm',
      'disinfectant',
      'coronavirus',
      'covid',
      'sanitizer',
      'hygiene',
      'bottle',
    ],
    category: ['hand', 'covid'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-128',
    previewURL:
      'https://ouch-cdn2.icons8.com/krS44xx-Zc94SAkxtewM2NyTGRrk3WN7tiZblh95WmQ/rs:fit:196:116/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvMjgw/LzdlZWI5MGQxLTNm/ZmYtNDlkNS1hZTYx/LTUwOThkN2YyM2Jj/Mi5wbmc.png',
    action: 'download',
  },
  icons8_3dhands__hands_sanitizer_black: {
    key: 'icons8_3dhands__hands_sanitizer_black',
    title: 'Hands Sanitizer',
    keywords: [
      'health',
      'hands',
      'palm',
      'disinfectant',
      'coronavirus',
      'covid',
      'sanitizer',
      'hygiene',
      'bottle',
    ],
    category: ['hand', 'covid'],
    collection: collections.icons8_3dhands,
    source: sources.icons8,
    downloadURL:
      'https://icons8.com/illustrations/illustration/3d-hands-fun-and-wild-130',
    previewURL:
      'https://ouch-cdn2.icons8.com/TrB783X5bEQl89Ibp_alYjdcki5_oFwJmEH0tHEMcgg/rs:fit:196:116/czM6Ly9pY29uczgu/b3VjaC1wcm9kLmFz/c2V0cy9wbmcvNjg4/L2VmZjFhY2Q3LTg4/MzQtNDkwMS04N2Fh/LTc5ZjdjZDI2MTcw/Yy5wbmc.png',
    action: 'download',
  },
  typedream_gradients__hue_1: {
    key: 'typedream_gradients__hue_1',
    title: 'Hue 1',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-1.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-1.png',
    action: 'use',
  },
  typedream_gradients__hue_1_gif: {
    key: 'typedream_gradients__hue_1_gif',
    title: 'Hue 1 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-1.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-1.gif',
    action: 'use',
  },
  typedream_gradients__hue_2: {
    key: 'typedream_gradients__hue_2',
    title: 'Hue 2',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-2.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-2.png',
    action: 'use',
  },
  typedream_gradients__hue_2_gif: {
    key: 'typedream_gradients__hue_2_gif',
    title: 'Hue 2 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-2.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-2.gif',
    action: 'use',
  },
  typedream_gradients__hue_3: {
    key: 'typedream_gradients__hue_3',
    title: 'Hue 3',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-3.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-3.png',
    action: 'use',
  },
  typedream_gradients__hue_3_gif: {
    key: 'typedream_gradients__hue_3_gif',
    title: 'Hue 3 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-3.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-3.gif',
    action: 'use',
  },
  typedream_gradients__hue_4: {
    key: 'typedream_gradients__hue_4',
    title: 'Hue 4',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-4.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-4.png',
    action: 'use',
  },
  typedream_gradients__hue_4_gif: {
    key: 'typedream_gradients__hue_4_gif',
    title: 'Hue 4 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-4.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-4.gif',
    action: 'use',
  },
  typedream_gradients__hue_5: {
    key: 'typedream_gradients__hue_5',
    title: 'Hue 5',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-5.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-5.png',
    action: 'use',
  },
  typedream_gradients__hue_5_gif: {
    key: 'typedream_gradients__hue_5_gif',
    title: 'Hue 5 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-5.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-5.gif',
    action: 'use',
  },
  typedream_gradients__hue_6: {
    key: 'typedream_gradients__hue_6',
    title: 'Hue 6',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-6.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-6.png',
    action: 'use',
  },
  typedream_gradients__hue_6_gif: {
    key: 'typedream_gradients__hue_6_gif',
    title: 'Hue 6 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-6.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-6.gif',
    action: 'use',
  },
  typedream_gradients__hue_7: {
    key: 'typedream_gradients__hue_7',
    title: 'Hue 7',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-7.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-7.png',
    action: 'use',
  },
  typedream_gradients__hue_7_gif: {
    key: 'typedream_gradients__hue_7_gif',
    title: 'Hue 7 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-7.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-7.gif',
    action: 'use',
  },
  typedream_gradients__hue_8: {
    key: 'typedream_gradients__hue_8',
    title: 'Hue 8',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-8.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-8.png',
    action: 'use',
  },
  typedream_gradients__hue_8_gif: {
    key: 'typedream_gradients__hue_8_gif',
    title: 'Hue 8 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-8.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-8.gif',
    action: 'use',
  },
  typedream_gradients__hue_9: {
    key: 'typedream_gradients__hue_9',
    title: 'Hue 9',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-9.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-9.png',
    action: 'use',
  },
  typedream_gradients__hue_9_gif: {
    key: 'typedream_gradients__hue_9_gif',
    title: 'Hue 9 - GIF',
    keywords: ['gradient', 'background', 'hue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-9.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/hue-9.gif',
    action: 'use',
  },
  typedream_gradients__blue_1: {
    key: 'typedream_gradients__blue_1',
    title: 'Blue 1',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-1.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-1.png',
    action: 'use',
  },
  typedream_gradients__blue_1_gif: {
    key: 'typedream_gradients__blue_1_gif',
    title: 'Blue 1 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-1.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-1.gif',
    action: 'use',
  },
  typedream_gradients__blue_2: {
    key: 'typedream_gradients__blue_2',
    title: 'Blue 2',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-2.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-2.png',
    action: 'use',
  },
  typedream_gradients__blue_2_gif: {
    key: 'typedream_gradients__blue_2_gif',
    title: 'Blue 2 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-2.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-2.gif',
    action: 'use',
  },
  typedream_gradients__blue_3: {
    key: 'typedream_gradients__blue_3',
    title: 'Blue 3',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-3.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-3.png',
    action: 'use',
  },
  typedream_gradients__blue_3_gif: {
    key: 'typedream_gradients__blue_3_gif',
    title: 'Blue 3 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-3.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-3.gif',
    action: 'use',
  },
  typedream_gradients__blue_4: {
    key: 'typedream_gradients__blue_4',
    title: 'Blue 4',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-4.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-4.png',
    action: 'use',
  },
  typedream_gradients__blue_4_gif: {
    key: 'typedream_gradients__blue_4_gif',
    title: 'Blue 4 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-4.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-4.gif',
    action: 'use',
  },
  typedream_gradients__blue_5: {
    key: 'typedream_gradients__blue_5',
    title: 'Blue 5',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-5.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-5.png',
    action: 'use',
  },
  typedream_gradients__blue_5_gif: {
    key: 'typedream_gradients__blue_5_gif',
    title: 'Blue 5 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-5.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-5.gif',
    action: 'use',
  },
  typedream_gradients__blue_6: {
    key: 'typedream_gradients__blue_6',
    title: 'Blue 6',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-6.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-6.png',
    action: 'use',
  },
  typedream_gradients__blue_6_gif: {
    key: 'typedream_gradients__blue_6_gif',
    title: 'Blue 6 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-6.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-6.gif',
    action: 'use',
  },
  typedream_gradients__blue_7: {
    key: 'typedream_gradients__blue_7',
    title: 'Blue 7',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-7.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-7.png',
    action: 'use',
  },
  typedream_gradients__blue_7_gif: {
    key: 'typedream_gradients__blue_7_gif',
    title: 'Blue 7 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-7.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-7.gif',
    action: 'use',
  },
  typedream_gradients__blue_8: {
    key: 'typedream_gradients__blue_8',
    title: 'Blue 8',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-8.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-8.png',
    action: 'use',
  },
  typedream_gradients__blue_8_gif: {
    key: 'typedream_gradients__blue_8_gif',
    title: 'Blue 8 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-8.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-8.gif',
    action: 'use',
  },
  typedream_gradients__blue_9: {
    key: 'typedream_gradients__blue_9',
    title: 'Blue 9',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-9.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-9.png',
    action: 'use',
  },
  typedream_gradients__blue_9_gif: {
    key: 'typedream_gradients__blue_9_gif',
    title: 'Blue 9 - GIF',
    keywords: ['gradient', 'background', 'blue'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-9.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/blue-9.gif',
    action: 'use',
  },
  typedream_gradients__green_1: {
    key: 'typedream_gradients__green_1',
    title: 'Green 1',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-1.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-1.png',
    action: 'use',
  },
  typedream_gradients__green_1_gif: {
    key: 'typedream_gradients__green_1_gif',
    title: 'Green 1 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-1.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-1.gif',
    action: 'use',
  },
  typedream_gradients__green_2: {
    key: 'typedream_gradients__green_2',
    title: 'Green 2',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-2.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-2.png',
    action: 'use',
  },
  typedream_gradients__green_2_gif: {
    key: 'typedream_gradients__green_2_gif',
    title: 'Green 2 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-2.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-2.gif',
    action: 'use',
  },
  typedream_gradients__green_3: {
    key: 'typedream_gradients__green_3',
    title: 'Green 3',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-3.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-3.png',
    action: 'use',
  },
  typedream_gradients__green_3_gif: {
    key: 'typedream_gradients__green_3_gif',
    title: 'Green 3 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-3.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-3.gif',
    action: 'use',
  },
  typedream_gradients__green_4: {
    key: 'typedream_gradients__green_4',
    title: 'Green 4',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-4.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-4.png',
    action: 'use',
  },
  typedream_gradients__green_4_gif: {
    key: 'typedream_gradients__green_4_gif',
    title: 'Green 4 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-4.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-4.gif',
    action: 'use',
  },
  typedream_gradients__green_5: {
    key: 'typedream_gradients__green_5',
    title: 'Green 5',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-5.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-5.png',
    action: 'use',
  },
  typedream_gradients__green_5_gif: {
    key: 'typedream_gradients__green_5_gif',
    title: 'Green 5 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-5.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-5.gif',
    action: 'use',
  },
  typedream_gradients__green_6: {
    key: 'typedream_gradients__green_6',
    title: 'Green 6',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-6.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-6.png',
    action: 'use',
  },
  typedream_gradients__green_6_gif: {
    key: 'typedream_gradients__green_6_gif',
    title: 'Green 6 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-6.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-6.gif',
    action: 'use',
  },
  typedream_gradients__green_7: {
    key: 'typedream_gradients__green_7',
    title: 'Green 7',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-7.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-7.png',
    action: 'use',
  },
  typedream_gradients__green_7_gif: {
    key: 'typedream_gradients__green_7_gif',
    title: 'Green 7 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-7.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-7.gif',
    action: 'use',
  },
  typedream_gradients__green_8: {
    key: 'typedream_gradients__green_8',
    title: 'Green 8',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-8.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-8.png',
    action: 'use',
  },
  typedream_gradients__green_8_gif: {
    key: 'typedream_gradients__green_8_gif',
    title: 'Green 8 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-8.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-8.gif',
    action: 'use',
  },
  typedream_gradients__green_9: {
    key: 'typedream_gradients__green_9',
    title: 'Green 9',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-9.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-9.png',
    action: 'use',
  },
  typedream_gradients__green_9_gif: {
    key: 'typedream_gradients__green_9_gif',
    title: 'Green 9 - GIF',
    keywords: ['gradient', 'background', 'green'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-9.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/green-9.gif',
    action: 'use',
  },
  typedream_gradients__red_1: {
    key: 'typedream_gradients__red_1',
    title: 'Red 1',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-1.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-1.png',
    action: 'use',
  },
  typedream_gradients__red_1_gif: {
    key: 'typedream_gradients__red_1_gif',
    title: 'Red 1 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-1.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-1.gif',
    action: 'use',
  },
  typedream_gradients__red_2: {
    key: 'typedream_gradients__red_2',
    title: 'Red 2',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-2.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-2.png',
    action: 'use',
  },
  typedream_gradients__red_2_gif: {
    key: 'typedream_gradients__red_2_gif',
    title: 'Red 2 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-2.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-2.gif',
    action: 'use',
  },
  typedream_gradients__red_3: {
    key: 'typedream_gradients__red_3',
    title: 'Red 3',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-3.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-3.png',
    action: 'use',
  },
  typedream_gradients__red_3_gif: {
    key: 'typedream_gradients__red_3_gif',
    title: 'Red 3 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-3.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-3.gif',
    action: 'use',
  },
  typedream_gradients__red_4: {
    key: 'typedream_gradients__red_4',
    title: 'Red 4',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-4.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-4.png',
    action: 'use',
  },
  typedream_gradients__red_4_gif: {
    key: 'typedream_gradients__red_4_gif',
    title: 'Red 4 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-4.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-4.gif',
    action: 'use',
  },
  typedream_gradients__red_5: {
    key: 'typedream_gradients__red_5',
    title: 'Red 5',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-5.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-5.png',
    action: 'use',
  },
  typedream_gradients__red_5_gif: {
    key: 'typedream_gradients__red_5_gif',
    title: 'Red 5 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-5.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-5.gif',
    action: 'use',
  },
  typedream_gradients__red_6: {
    key: 'typedream_gradients__red_6',
    title: 'Red 6',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-6.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-6.png',
    action: 'use',
  },
  typedream_gradients__red_6_gif: {
    key: 'typedream_gradients__red_6_gif',
    title: 'Red 6 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-6.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-6.gif',
    action: 'use',
  },
  typedream_gradients__red_7: {
    key: 'typedream_gradients__red_7',
    title: 'Red 7',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-7.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-7.png',
    action: 'use',
  },
  typedream_gradients__red_7_gif: {
    key: 'typedream_gradients__red_7_gif',
    title: 'Red 7 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-7.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-7.gif',
    action: 'use',
  },
  typedream_gradients__red_8: {
    key: 'typedream_gradients__red_8',
    title: 'Red 8',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-8.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-8.png',
    action: 'use',
  },
  typedream_gradients__red_8_gif: {
    key: 'typedream_gradients__red_8_gif',
    title: 'Red 8 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-8.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-8.gif',
    action: 'use',
  },
  typedream_gradients__red_9: {
    key: 'typedream_gradients__red_9',
    title: 'Red 9',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-9.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-9.png',
    action: 'use',
  },
  typedream_gradients__red_9_gif: {
    key: 'typedream_gradients__red_9_gif',
    title: 'Red 9 - GIF',
    keywords: ['gradient', 'background', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-9.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/red-9.gif',
    action: 'use',
  },
  typedream_gradients__dark_1: {
    key: 'typedream_gradients__dark_1',
    title: 'Dark 1',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-1.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-1.png',
    action: 'use',
  },
  typedream_gradients__dark_1_gif: {
    key: 'typedream_gradients__dark_1_gif',
    title: 'Dark 1 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-1.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-1.gif',
    action: 'use',
  },
  typedream_gradients__dark_2: {
    key: 'typedream_gradients__dark_2',
    title: 'Dark 2',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-2.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-2.png',
    action: 'use',
  },
  typedream_gradients__dark_2_gif: {
    key: 'typedream_gradients__dark_2_gif',
    title: 'Dark 2 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-2.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-2.gif',
    action: 'use',
  },
  typedream_gradients__dark_3: {
    key: 'typedream_gradients__dark_3',
    title: 'Dark 3',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-3.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-3.png',
    action: 'use',
  },
  typedream_gradients__dark_3_gif: {
    key: 'typedream_gradients__dark_3_gif',
    title: 'Dark 3 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-3.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-3.gif',
    action: 'use',
  },
  typedream_gradients__dark_4: {
    key: 'typedream_gradients__dark_4',
    title: 'Dark 4',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-4.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-4.png',
    action: 'use',
  },
  typedream_gradients__dark_4_gif: {
    key: 'typedream_gradients__dark_4_gif',
    title: 'Dark 4 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-4.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-4.gif',
    action: 'use',
  },
  typedream_gradients__dark_5: {
    key: 'typedream_gradients__dark_5',
    title: 'Dark 5',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-5.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-5.png',
    action: 'use',
  },
  typedream_gradients__dark_5_gif: {
    key: 'typedream_gradients__dark_5_gif',
    title: 'Dark 5 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-5.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-5.gif',
    action: 'use',
  },
  typedream_gradients__dark_6: {
    key: 'typedream_gradients__dark_6',
    title: 'Dark 6',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-6.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-6.png',
    action: 'use',
  },
  typedream_gradients__dark_6_gif: {
    key: 'typedream_gradients__dark_6_gif',
    title: 'Dark 6 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-6.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-6.gif',
    action: 'use',
  },
  typedream_gradients__dark_7: {
    key: 'typedream_gradients__dark_7',
    title: 'Dark 7',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-7.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-7.png',
    action: 'use',
  },
  typedream_gradients__dark_7_gif: {
    key: 'typedream_gradients__dark_7_gif',
    title: 'Dark 7 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-7.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-7.gif',
    action: 'use',
  },
  typedream_gradients__dark_8: {
    key: 'typedream_gradients__dark_8',
    title: 'Dark 8',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-8.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-8.png',
    action: 'use',
  },
  typedream_gradients__dark_8_gif: {
    key: 'typedream_gradients__dark_8_gif',
    title: 'Dark 8 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-8.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-8.gif',
    action: 'use',
  },
  typedream_gradients__dark_9: {
    key: 'typedream_gradients__dark_9',
    title: 'Dark 9',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-9.png',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-9.png',
    action: 'use',
  },
  typedream_gradients__dark_9_gif: {
    key: 'typedream_gradients__dark_9_gif',
    title: 'Dark 9 - GIF',
    keywords: ['gradient', 'background', 'dark'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-9.gif',
    useURL:
      'https://typedream-assets.sfo3.digitaloceanspaces.com/illustrations/gradients/dark-9.gif',
    action: 'use',
  },
  typedream_gradients__blue: {
    key: 'typedream_gradients__blue',
    title: 'Diamond',
    keywords: ['gradient', 'background', 'blue', 'diamond'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/blue.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/blue.png',
    action: 'use',
  },
  typedream_gradients__black: {
    key: 'typedream_gradients__black',
    title: 'Charcoal',
    keywords: ['gradient', 'background', 'black', 'gray', 'charcoal'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/black.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/black.png',
    action: 'use',
  },
  typedream_gradients__white: {
    key: 'typedream_gradients__white',
    title: 'Cloud',
    keywords: ['gradient', 'background', 'white', 'cloud', 'gray'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/white.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/white.png',
    action: 'use',
  },
  typedream_gradients__green: {
    key: 'typedream_gradients__green',
    title: 'Grass',
    keywords: ['gradient', 'background', 'green', 'grass'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/green.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/green.png',
    action: 'use',
  },
  typedream_gradients__indigo: {
    key: 'typedream_gradients__indigo',
    title: 'Blueberry',
    keywords: [
      'gradient',
      'background',
      'indigo',
      'blueberry',
      'blue',
      'purple',
    ],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/indigo.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/indigo.png',
    action: 'use',
  },
  typedream_gradients__purple: {
    key: 'typedream_gradients__purple',
    title: 'Lavender',
    keywords: [
      'gradient',
      'background',
      'purple',
      'lavender',
      'red',
      'pink',
      'blue',
    ],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/purple.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/purple.png',
    action: 'use',
  },
  typedream_gradients__red: {
    key: 'typedream_gradients__red',
    title: 'Wine',
    keywords: ['gradient', 'background', 'red', 'wine', 'pink', 'orange'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/red.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/red.png',
    action: 'use',
  },
  typedream_gradients__orange: {
    key: 'typedream_gradients__orange',
    title: 'Coral',
    keywords: ['gradient', 'background', 'orange', 'coral', 'red', 'yellow'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/orange.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/orange.png',
    action: 'use',
  },
  typedream_gradients__pink: {
    key: 'typedream_gradients__pink',
    title: 'Rose',
    keywords: ['gradient', 'background', 'pink', 'rose', 'red'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/pink.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/pink.png',
    action: 'use',
  },
  typedream_gradients__yellow: {
    key: 'typedream_gradients__yellow',
    title: 'Banana',
    keywords: ['gradient', 'background', 'yellow', 'banana', 'orange'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/yellow.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/yellow.png',
    action: 'use',
  },
  typedream_gradients__1: {
    key: 'typedream_gradients__1',
    title: 'Cosmos',
    keywords: ['gradient', 'background', 'blue', 'pink', 'red', 'purple'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/gradient-1.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/gradient-1.png',
    action: 'use',
  },
  typedream_gradients__2: {
    key: 'typedream_gradients__2',
    title: 'Nebula',
    keywords: ['gradient', 'background', 'blue', 'pink', 'red', 'purple'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/gradient-2.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/gradient-2.png',
    action: 'use',
  },
  typedream_gradients__7: {
    key: 'typedream_gradients__7',
    title: 'Cotton',
    keywords: ['gradient', 'background', 'orange', 'purple', 'yellow'],
    category: ['gradient', 'background'],
    collection: collections.typedream_gradients,
    source: sources.typedream,
    previewURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/gradient-7.png',
    useURL:
      'https://typedream.sfo3.digitaloceanspaces.com/illustrations/gradients/gradient-7.png',
    action: 'use',
  },
};
